import { isBuiltInDatabaseLayoutType } from '@databases/types/database/view';
import { err, ok, type Result } from 'neverthrow';

/**
 * Reason codes returned when a layout write is rejected.
 *
 * - `VIEW_LAYOUT_NOT_FOUND` — the layout ID is unknown to the policy.
 * - `VIEW_LAYOUT_WRITE_DISABLED` — the layout is recognised but currently
 *   read-only (e.g. an extension module in read-only mode).
 */
export type ViewLayoutWritePolicyErrorReason =
  | 'VIEW_LAYOUT_NOT_FOUND'
  | 'VIEW_LAYOUT_WRITE_DISABLED';

/**
 * Error payload returned by {@link ViewLayoutWritePolicy.requireWritable} when
 * a layout ID is not writable.
 */
export type ViewLayoutWritePolicyError = {
  layoutId: string;
  reason: ViewLayoutWritePolicyErrorReason;
  message: string;
};

/**
 * Narrow contract the SDK uses to gate view mutations that carry a layout type.
 *
 * The SDK calls `requireWritable` before `add()` and `update()` to decide
 * whether a given layout ID is allowed. Callers can inject a custom policy
 * (e.g. one backed by `@databases/view-layout-modules`) to enable extension
 * layouts; the SDK itself only ships {@link builtInOnlyWritePolicy}.
 */
export interface ViewLayoutWritePolicy {
  requireWritable(layoutId: string): Result<void, ViewLayoutWritePolicyError>;
}

/**
 * Default policy that permits only the four built-in layout types
 * (table, gallery, board, calendar). Any other layout ID is rejected
 * with `VIEW_LAYOUT_NOT_FOUND`.
 */
export const builtInOnlyWritePolicy: ViewLayoutWritePolicy = {
  requireWritable(layoutId) {
    if (isBuiltInDatabaseLayoutType(layoutId)) {
      return ok(undefined);
    }

    return err({
      layoutId,
      reason: 'VIEW_LAYOUT_NOT_FOUND',
      message: `Layout "${layoutId}" is not a built-in layout type`,
    });
  },
};
